import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useDebounce } from 'use-debounce';
import { useQueryParam, NumberParam, StringParam } from 'use-query-params';
import { Button, Form, Table } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useToastContext, Toast } from 'components/Toast';
import * as api from 'utils/api';
import Sidebar from 'components/Common/Sidebar';
import { useAppContext } from 'components/AppContext';
import CustomPagination from 'components/Common/CustomPagination';
import { getLocaleDateString } from 'utils/time';
import { ITEMS_PER_PAGE } from 'constants/common';
import styles from './BmsListings.module.scss';

export default function BmsListings() {
  const history = useHistory();
  const { request } = useAppContext();
  const { toastError } = useToastContext();
  const [listings, setListings] = useState([]);
  const [page = 1, setPage] = useQueryParam('page', NumberParam);
  const [numPages, setNumPages] = useState(0);
  const [searchText, setSearchText] = useQueryParam('search', StringParam);
  const [bmsAppUrl, setBmsAppUrl] = useState('http://localhost:3004');

  const [debouncedSearchText] = useDebounce(searchText, 500, {
    leading: true,
    trailing: true,
  });

  const fetchListings = useCallback(async () => {
    const { error, result, success } = await request(api.getBmsListings({
      limit: ITEMS_PER_PAGE,
      offset: (page - 1) * ITEMS_PER_PAGE,
      search: debouncedSearchText,
    }));

    if (success) {
      setListings(result.listings);
      setNumPages(Math.ceil(result.count / ITEMS_PER_PAGE));
      if (result.bmsAppUrl) {
        setBmsAppUrl(result.bmsAppUrl);
      }
    } else {
      toastError(error);
    }
  }, [toastError, page, request, debouncedSearchText]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings, page]);

  const onImpersonate = async (e, ownerId, teamId, listingId) => {
    e.stopPropagation();
    const { success, result, error } = await request(api.getLoginAsUserUrl(ownerId));
    if (success) {
      try {
        const urlObj = new URL(result.url);
        const token = urlObj.searchParams.get('access_token');
        const targetUrl = `${bmsAppUrl}/teams/${teamId}/listings/${listingId}?access_token=${token}`;
        window.open(targetUrl);
      } catch (err) {
        toastError('Failed to parse redirect URL from backend');
      }
    } else {
      toastError(error || 'Failed to request login session');
    }
  };

  const renderStatus = (status) => {
    let className = styles.draft;
    if (status === 'pending') className = styles.pending;
    else if (status === 'delivered') className = styles.delivered;

    return (
      <span className={`${styles.statusBadge} ${className}`}>
        {status || 'draft'}
      </span>
    );
  };

  return (
    <div className="main">
      <Sidebar />
      <div className="right-part">
        <Toast />
        <div className={styles.listings}>
          <div className={styles.header}>
            <div className="title">BMS Listings</div>
          </div>

          <div className={styles.filters}>
            <Form.Group className={styles.searchGroup}>
              <Form.Label>Search listings</Form.Label>
              <Form.Control
                placeholder="Search by address or property name..."
                value={searchText || ''}
                onChange={(e) => setSearchText(e.target.value || undefined)}
              />
            </Form.Group>
          </div>

          <div className={styles.tableCard}>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Property / Address</th>
                  <th>City / State</th>
                  <th>Team / Org</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => (
                  <tr
                    key={listing.id}
                    onClick={() => history.push(`/bms/listings/${listing.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{listing.id}</td>
                    <td>
                      <strong>
                        {listing.customPropertyName || listing.addressLine1 || `Listing #${listing.id}`}
                      </strong>
                    </td>
                    <td>
                      {listing.city || listing.state
                        ? `${listing.city || ''}${listing.city && listing.state ? ', ' : ''}${listing.state || ''}`
                        : '—'}
                    </td>
                    <td>
                      {listing.team?.name || '—'}
                    </td>
                    <td>
                      {listing.customer
                        ? `${listing.customer.firstName || ''} ${listing.customer.lastName || ''}`
                        : '—'}
                    </td>
                    <td>{renderStatus(listing.status)}</td>
                    <td>
                      {moment.utc(listing.createdAt).local().format(`${getLocaleDateString()} HH:mm`)}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => history.push(`/bms/listings/${listing.id}`)}
                        >
                          View
                        </Button>
                        {listing.team?.owner?.id && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => onImpersonate(e, listing.team.owner.id, listing.team.id, listing.id)}
                          >
                            Impersonate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {listings.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      No listings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="pagination-wrapper">
            <CustomPagination
              page={page}
              setPage={setPage}
              numPages={numPages}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
